import React, { useMemo, useState, useCallback, useEffect, useRef } from "react";
import { Palette } from "lucide-react";
import { DeleteIconButton } from "components/DatatablesActionIcons";
import { showSuccess, showError } from "toast/toast";

/** ------------ Types (keep in your shared types.ts if you want) ------------ */
import { getAuthCookie } from "../utils/authCookies";

export type ProductCombination = {
  id: string;
  sno?: number; // optional, if you have S.NO from backend
  attributes: Record<string, string>; // { size: "18X14", material: "Reflective" }
  price?: string | number;
};

export type DesignOption = {
  id: string;
  name: string;
  category?: string; // "Modern"
  /** Parent folder label for summary (replaces category display there when set). */
  parentFolderName?: string;
};

export type DesignAssignment = {
  combinationId: string;
  designId: string;
  /** Immediate parent folder label when picked from the folder tree (summary column). */
  parentFolderName?: string;
};

type TemplateOption = {
  value: string;
  label: string;
  category?: string;
  previewUrl?: string;
  parentFolderName?: string;
};

type Props = {
  productType: string;
  mode: "add" | "edit";
  combinations: ProductCombination[];

  /** If not passed, component will show a small mock list */
  designs?: DesignOption[];

  /** if you already have saved assignments (Edit screen), pass here */
  initialAssignments?: DesignAssignment[];

  /** callback whenever assignment changes */
  onDesignAssignmentChange?: (assignments: DesignAssignment[]) => void;

  /** callback for single assignment event (optional) */
  onDesignAssignment?: (combinationId: string, designId: string) => void;

  /** Optional heading override */
  title?: string;

  /** Product ID for API calls */
  productId?: string;

  /** Variant ID (for simple products) or variant IDs (for variable products) */
  variantId?: string;

};

/** ------------ Helpers ------------ */
const formatVariationLabel = (attributes: Record<string, string>, variantName?: string) => {
  // Return only the variant name without attributes
  if (variantName) {
    return variantName;
  }
  return "Default Variation";
};

const money = (v?: string | number) => {
  if (v === null || v === undefined || v === "") return "$0";
  const n = typeof v === "string" ? Number(v) : v;
  if (Number.isNaN(n)) return `$${v}`;
  return `$${n}`;
};

/** ------------ Component ------------ */
const DesignsTab: React.FC<Props> = ({
  productType,
  mode,
  combinations,
  designs,
  initialAssignments,
  onDesignAssignmentChange,
  onDesignAssignment,
  title = "Design Assignment",
  productId,
  variantId,
}) => {
  const token = getAuthCookie("sessionToken");
  /** folders-designs/select2 `limit` (server default is 10; we use 50 to reduce paging while browsing) */
  const FOLDER_PAGE_SIZE = 50;
  const [assigning, setAssigning] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [loadingAssignments, setLoadingAssignments] = useState(false);

  const [selectedVariationId, setSelectedVariationId] = useState<string>("");
  const [assignments, setAssignments] = useState<DesignAssignment[]>(
    initialAssignments ?? []
  );
  const [selectedTemplates, setSelectedTemplates] = useState<TemplateOption[]>([]);
  const [loadedTemplates, setLoadedTemplates] = useState<Map<string, TemplateOption>>(new Map());
  const [treeItems, setTreeItems] = useState<
    Array<{
      kind: "folder" | "design";
      id: string;
      label: string;
      depth: number;
      previewUrl?: string;
      category?: string;
      parentFolderName?: string;
    }>
  >([]);
  const [expandedFolderIds, setExpandedFolderIds] = useState<Set<string>>(new Set());
  const [loadingFolderIds, setLoadingFolderIds] = useState<Set<string>>(new Set());
  const treeItemsRef = useRef(treeItems);
  treeItemsRef.current = treeItems;
  type FetchedAssignmentItem = {
    variantId: string;
    variantName: string;
    attributes: Record<string, string>;
    templates: Array<{
      template_id: string;
      template_slug: string;
      /** Resolved label: design_name, else template_slug, else template_id */
      designDisplayName: string;
      category?: string;
      parentFolderName?: string;
    }>;
  };
  const [fetchedAssignments, setFetchedAssignments] = useState<FetchedAssignmentItem[]>([]);

  // Fetch assigned templates function (reusable)
  const fetchAssignedTemplates = useCallback(async () => {
    if (!productId) return null;
    
    setLoadingAssignments(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/realesy-studio/product-variant-templates?product_id=${productId}`,
        {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        console.error("Failed to fetch assigned templates:", response.statusText);
        return null;
      }

      const data = await response.json();
      const assignmentsData: FetchedAssignmentItem[] = (data?.data || []).map((item: any) => ({
        variantId: item.variant_id || "",
        variantName: item.variant_name || "",
        attributes: item.attributes || {},
        templates: (item.templates || []).map((t: any) => {
          // API returns folder + design on each template; optional legacy `designs` array on variant.
          const design = item.designs?.find(
            (d: any) => d._id === t.design_id || d.id === t.design_id
          );
          const designDisplayName =
            String(t.design_name || "").trim() ||
            String(t.template_slug || "").trim() ||
            String(t.template_id || "");
          const categoryFromTemplate = String(t.category ?? "").trim();
          const folderFromTemplate = String(t.folder_name ?? "").trim();
          return {
            template_id: t.template_id || "",
            template_slug: t.template_slug || "",
            designDisplayName,
            category:
              categoryFromTemplate ||
              (design?.category != null && String(design.category).trim()) ||
              undefined,
            parentFolderName:
              folderFromTemplate ||
              (design?.folder_name && String(design.folder_name)) ||
              (design?.parent_folder_name && String(design.parent_folder_name)) ||
              (design?.folder?.name && String(design.folder.name)) ||
              undefined,
          };
        }),
      }));

      setFetchedAssignments(assignmentsData);

      // Seed loadedTemplates so labels/categories render even before the folder tree loads.
      setLoadedTemplates((prev) => {
        const updated = new Map(prev);
        assignmentsData.forEach((item) => {
          item.templates.forEach((t) => {
            updated.set(t.template_id, {
              value: t.template_id,
              label: t.designDisplayName,
              category: t.category,
              parentFolderName: t.parentFolderName,
            });
          });
        });
        return updated;
      });

      // Also populate assignments state for compatibility
      const newAssignments: DesignAssignment[] = [];
      assignmentsData.forEach((item: any) => {
        item.templates.forEach((t: any) => {
          newAssignments.push({
            combinationId: item.variantId,
            designId: t.template_id,
            parentFolderName: t.parentFolderName,
          });
        });
      });
      setAssignments(newAssignments);
      
      return assignmentsData;
    } catch (err) {
      console.error("Error fetching assigned templates:", err);
      return null;
    } finally {
      setLoadingAssignments(false);
    }
  }, [productId, token]);

  // Fetch assigned templates in edit mode on mount
  useEffect(() => {
    if (mode === "edit" && productId) {
      fetchAssignedTemplates();
    }
  }, [mode, productId, fetchAssignedTemplates]);

  // Convert templates to DesignOption format for compatibility
  const designOptions: DesignOption[] = useMemo(() => {
    const templateArray = Array.from(loadedTemplates.values());
    return templateArray.map((template) => ({
      id: template.value,
      name: template.label,
      category: template.category,
      parentFolderName: template.parentFolderName,
    }));
  }, [loadedTemplates]);

  const templateOptionFromTreeNode = useCallback((node: {
    id: string;
    label: string;
    category?: string;
    previewUrl?: string;
    parentFolderName?: string;
  }): TemplateOption => {
    return {
      value: node.id,
      label: node.label,
      category: node.category,
      previewUrl: node.previewUrl,
      parentFolderName: node.parentFolderName,
    };
  }, []);

  const fetchFolderTreeChildren = useCallback(
    async (
      parentId: string,
      depth: number,
      opts?: { q?: string; page?: number },
      /** Label of the folder whose children we are loading (designs get this as parent folder name). */
      parentFolderLabel?: string
    ) => {
      const page = opts?.page ?? 1;
      const q = (opts?.q ?? "").trim();
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      params.set("page", String(page));
      params.set("limit", String(FOLDER_PAGE_SIZE));
      params.set("parent_id", parentId);
      params.set("is_template", "true");
      const url = `${process.env.REACT_APP_API_URL}/realesy-studio/folders-designs/select2?${params.toString()}`;
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch folder tree for parent_id=${parentId}`);
      }
      const json = await response.json();
      const rawResults: any[] = json?.results || [];

      const mapped = rawResults
        .filter((r) => r?.type === "folder" || r?.type === "design")
        .map((r) => {
          if (r.type === "folder") {
            return {
              kind: "folder" as const,
              id: String(r.id),
              label: String(r.text || r.name || "Untitled Folder"),
              depth,
            };
          }
          const fromApi =
            (r.folder_name && String(r.folder_name)) ||
            (r.parent_folder_name && String(r.parent_folder_name)) ||
            (r.folder?.name && String(r.folder.name)) ||
            undefined;
          return {
            kind: "design" as const,
            id: String(r.id),
            label: String(r.text || r.name || "Untitled Design"),
            depth,
            previewUrl: r.preview_url || r.thumbnail_url || "",
            category: r.category,
            parentFolderName: parentFolderLabel || fromApi,
          };
        });

      // Seed loadedTemplates with any design nodes returned.
      const designNodes = mapped.filter((m) => m.kind === "design");
      if (designNodes.length) {
        setLoadedTemplates((prev) => {
          const updated = new Map(prev);
          designNodes.forEach((n) => {
            const existing = updated.get(n.id);
            const nextOption = templateOptionFromTreeNode({
              id: n.id,
              label: n.label,
              category: n.category,
              previewUrl: n.previewUrl,
              parentFolderName: n.parentFolderName,
            });
            // Prefer the version with previewUrl if we already have a label.
            updated.set(n.id, {
              ...existing,
              ...nextOption,
              previewUrl: nextOption.previewUrl || existing?.previewUrl,
              parentFolderName: nextOption.parentFolderName || existing?.parentFolderName,
            });
          });
          return updated;
        });
      }

      return mapped;
    },
    [FOLDER_PAGE_SIZE, token, templateOptionFromTreeNode]
  );

  // Load root folder tree on mount.
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        setExpandedFolderIds(new Set());
        setLoadingFolderIds(new Set());
        const rootNodes = await fetchFolderTreeChildren("root", 0);
        if (cancelled) return;
        setTreeItems(rootNodes);
      } catch {
        if (cancelled) return;
        setTreeItems([]);
      }
    };
    if (token) run();
    return () => {
      cancelled = true;
    };
  }, [token, fetchFolderTreeChildren]);

  const selectedVariation = useMemo(() => {
    return combinations?.find((c) => c.id === selectedVariationId);
  }, [combinations, selectedVariationId]);

  const assignedDesignIdsForSelectedVariation = useMemo(() => {
    if (!selectedVariationId) return [];
    return assignments
      .filter((a) => a.combinationId === selectedVariationId)
      .map((a) => a.designId);
  }, [assignments, selectedVariationId]);

  const assignedDesignsForSelectedVariation = useMemo(() => {
    if (!selectedVariationId || assignedDesignIdsForSelectedVariation.length === 0) return [];
    return assignedDesignIdsForSelectedVariation.map((id) => {
      const tpl = loadedTemplates.get(id);
      const asn = assignments.find((a) => a.combinationId === selectedVariationId && a.designId === id);
      return {
        id,
        name: tpl?.label || id,
        category: tpl?.category,
        parentFolderName: asn?.parentFolderName ?? tpl?.parentFolderName,
      } as DesignOption;
    });
  }, [assignments, loadedTemplates, assignedDesignIdsForSelectedVariation, selectedVariationId]);

  const handleVariationChange = (id: string) => {
    setSelectedVariationId(id);
    const assignedIds = assignments
      .filter((a) => a.combinationId === id)
      .map((a) => a.designId);

    const templates: TemplateOption[] = assignedIds.map((designId) => {
      const asn = assignments.find((a) => a.combinationId === id && a.designId === designId);
      const template = loadedTemplates.get(designId);
      if (template) {
        return {
          ...template,
          parentFolderName: asn?.parentFolderName ?? template.parentFolderName,
        };
      }
      const designFromProps = designs?.find((d) => d.id === designId);
      if (designFromProps)
        return {
          value: designFromProps.id,
          label: designFromProps.name,
          parentFolderName: asn?.parentFolderName ?? designFromProps.parentFolderName,
        };
      return { value: designId, label: designId, parentFolderName: asn?.parentFolderName };
    });

    setSelectedTemplates(templates);
  };

  const syncAssignmentsForSelectedVariation = useCallback(
    (nextSelectedTemplates: TemplateOption[]) => {
      if (!selectedVariationId) return;
      const seen = new Set<string>();
      const deduped = nextSelectedTemplates.filter((t) => {
        if (seen.has(t.value)) return false;
        seen.add(t.value);
        return true;
      });

      setAssignments((prev) => {
        const withoutCurrent = prev.filter((a) => a.combinationId !== selectedVariationId);
        const newAssignments: DesignAssignment[] = deduped.map((t) => ({
          combinationId: selectedVariationId,
          designId: t.value,
          parentFolderName: t.parentFolderName,
        }));
        const next = [...withoutCurrent, ...newAssignments];

        onDesignAssignmentChange?.(next);
        newAssignments.forEach((a) => onDesignAssignment?.(a.combinationId, a.designId));
        return next;
      });
    },
    [onDesignAssignment, onDesignAssignmentChange, selectedVariationId]
  );

  const toggleDesignSelection = useCallback(
    (designNode: {
      id: string;
      label: string;
      category?: string;
      previewUrl?: string;
      parentFolderName?: string;
    }) => {
      if (!selectedVariationId) return;

      const option = templateOptionFromTreeNode(designNode);
      setLoadedTemplates((prev) => {
        const next = new Map(prev);
        const existing = next.get(option.value);
        next.set(option.value, { ...existing, ...option });
        return next;
      });
      const exists = selectedTemplates.some((t) => t.value === option.value);

      // Each variant can only have ONE design linked.
      // If the user tries to add a second design (not a deselect), block it.
      if (!exists && selectedTemplates.length > 0) {
        showError("You already have a design assigned to this variant. Please remove it before assigning a new one.");
        return;
      }

      const nextSelectedTemplates = exists
        ? selectedTemplates.filter((t) => t.value !== option.value)
        : [option];

      setSelectedTemplates(nextSelectedTemplates);
      syncAssignmentsForSelectedVariation(nextSelectedTemplates);
    },
    [selectedTemplates, selectedVariationId, syncAssignmentsForSelectedVariation, templateOptionFromTreeNode]
  );

  /** Select2-style folder rows: first click expands (loads children under the row); second click collapses and removes nested rows. */
  const handleFolderToggle = useCallback(
    async (folderId: string) => {
      if (loadingFolderIds.has(folderId)) return;

      if (expandedFolderIds.has(folderId)) {
        const prev = treeItemsRef.current;
        const idx = prev.findIndex((i) => i.kind === "folder" && i.id === folderId);
        if (idx === -1) return;
        const parentDepth = prev[idx].depth;
        let end = idx + 1;
        const removedNestedFolderIds: string[] = [];
        while (end < prev.length && prev[end].depth > parentDepth) {
          if (prev[end].kind === "folder") removedNestedFolderIds.push(prev[end].id);
          end += 1;
        }
        setTreeItems([...prev.slice(0, idx + 1), ...prev.slice(end)]);
        setExpandedFolderIds((exp) => {
          const next = new Set(exp);
          next.delete(folderId);
          removedNestedFolderIds.forEach((id) => next.delete(id));
          return next;
        });
        return;
      }

      const prev = treeItemsRef.current;
      const idx = prev.findIndex((i) => i.kind === "folder" && i.id === folderId);
      if (idx === -1) return;
      const depth = prev[idx]?.depth ?? 0;

      setLoadingFolderIds((s) => new Set(s).add(folderId));
      try {
        const folderLabel = prev[idx]?.label ?? "";
        const children = await fetchFolderTreeChildren(folderId, depth + 1, undefined, folderLabel);
        setExpandedFolderIds((exp) => new Set(exp).add(folderId));
        setTreeItems((p) => {
          const folderIdx = p.findIndex((i) => i.kind === "folder" && i.id === folderId);
          if (folderIdx === -1) return p;
          return [...p.slice(0, folderIdx + 1), ...children, ...p.slice(folderIdx + 1)];
        });
      } catch {
        // no-op
      } finally {
        setLoadingFolderIds((s) => {
          const next = new Set(s);
          next.delete(folderId);
          return next;
        });
      }
    },
    [expandedFolderIds, fetchFolderTreeChildren, loadingFolderIds]
  );

  const summaryRows = useMemo(() => {
    // In edit mode, merge fetched assignments with newly assigned templates
    if (mode === "edit") {
      const rows: { combo: ProductCombination; design: DesignOption }[] = [];
      const processedAssignments = new Set<string>(); // Track processed variantId-templateId pairs

      // First, add all fetched assignments from API
      fetchedAssignments.forEach((item) => {
        // Find or create combination
        let combo = combinations.find((c) => c.id === item.variantId);
        if (!combo) {
          combo = {
            id: item.variantId,
            attributes: item.attributes,
            variant_name: item.variantName,
          } as ProductCombination & { variant_name?: string };
        }

        // Add each template as a row
        item.templates.forEach((t) => {
          const key = `${item.variantId}-${t.template_id}`;
          processedAssignments.add(key);
          rows.push({
            combo,
            design: {
              id: t.template_id,
              name: t.designDisplayName,
              category: t.category,
              parentFolderName:
                t.parentFolderName ?? loadedTemplates.get(t.template_id)?.parentFolderName,
            },
          });
        });
      });

      // Then, add newly assigned templates that aren't in fetchedAssignments yet
      assignments.forEach((assignment) => {
        const key = `${assignment.combinationId}-${assignment.designId}`;
        if (!processedAssignments.has(key)) {
          // Find or create combination
          let combo = combinations.find((c) => c.id === assignment.combinationId);
          if (!combo) {
            // Try to find in fetchedAssignments for variant details
            const fetchedItem = fetchedAssignments.find((f) => f.variantId === assignment.combinationId);
            combo = {
              id: assignment.combinationId,
              attributes: fetchedItem?.attributes || {},
              variant_name: fetchedItem?.variantName,
            } as ProductCombination & { variant_name?: string };
          }

          // Get template details from loadedTemplates
          const template = loadedTemplates.get(assignment.designId);
          if (template) {
            rows.push({
              combo,
              design: {
                id: template.value,
                name: template.label,
                category: template.category,
                parentFolderName: assignment.parentFolderName ?? template.parentFolderName,
              },
            });
          } else {
            // Fallback: create minimal design object
            rows.push({
              combo,
              design: {
                id: assignment.designId,
                name: assignment.designId,
                category: undefined,
                parentFolderName: assignment.parentFolderName,
              },
            });
          }
        }
      });

      return rows;
    }

    // In add mode, use assignments state
    return assignments
      .map((a) => {
        const combo = combinations.find((c) => c.id === a.combinationId);
        if (!combo) return null;
        
        // Try to find design in loaded templates first
        const template = loadedTemplates.get(a.designId);
        if (template) {
          const design: DesignOption = {
            id: template.value,
            name: template.label,
            category: template.category,
            parentFolderName: template.parentFolderName ?? a.parentFolderName,
          };
          return { combo, design };
        }
        
        // Fallback to designOptions (from props or derived from templates)
        const design = designOptions.find((d) => d.id === a.designId);
        if (design) {
          return {
            combo,
            design: {
              ...design,
              parentFolderName: a.parentFolderName ?? design.parentFolderName,
            },
          };
        }
        
        // If not found, create a minimal design object from the ID
        const designFromId: DesignOption = {
          id: a.designId,
          name: a.designId, // Will show ID until template is loaded
          category: undefined,
          parentFolderName: a.parentFolderName,
        };
        return { combo, design: designFromId };
      })
      .filter(Boolean) as { combo: ProductCombination; design: DesignOption }[];
  }, [mode, fetchedAssignments, assignments, combinations, designOptions, loadedTemplates]);

  const showEmptyCombinations = combinations?.length === 0;

  // Assign templates to product/variant (bulk assign)
  const handleAssignDesigns = async () => {
    if (!productId || assignments.length === 0) {
      showError("Please select variations and assign designs first.");
      return;
    }

    setAssigning(true);
    try {
      // Group assignments by variant_id (combinationId) and collect template IDs
      const assignmentsByVariant: Record<string, string[]> = {};
      
      assignments.forEach((assignment) => {
        const variantId = assignment.combinationId;
        if (!assignmentsByVariant[variantId]) {
          assignmentsByVariant[variantId] = [];
        }
        assignmentsByVariant[variantId].push(assignment.designId);
      });

      const payload = {
        product_id: productId,
        assignments: assignmentsByVariant,
      };

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/realesy-studio/product-templates/assign-bulk`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        }
      );

      const result = await response.json();
      if (!response.ok || result?.error) {
        throw new Error(result?.error || result?.message || "Failed to assign designs");
      }

      showSuccess("Designs assigned successfully!");

      // In edit mode, refetch assigned templates to update the table
      if (mode === "edit") {
        await fetchAssignedTemplates();
      }
    } catch (err: any) {
      console.error("Assign designs error:", err);
      showError(err?.message || "Failed to assign designs");
    } finally {
      setAssigning(false);
    }
  };

  // Unassign template from product/variant
  const handleDeleteAssignment = async (combinationId: string, designId: string) => {
    if (!productId || !combinationId) {
      showError("Product or variant information is missing.");
      return;
    }

    const deleteKey = `${combinationId}-${designId}`;
    setDeleting(deleteKey);
    try {
      const payload = {
        template_id: designId,
        product_id: productId,
        variant_id: combinationId,
      };

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/realesy-studio/product-templates/unassign`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        }
      );

      const result = await response.json();
      if (!response.ok || result?.error) {
        throw new Error(result?.error || result?.message || "Failed to unassign template");
      }

      // Remove from local state
      const updatedAssignments = assignments.filter(
        (a) => !(a.combinationId === combinationId && a.designId === designId)
      );
      setAssignments(updatedAssignments);
      onDesignAssignmentChange?.(updatedAssignments);

      // Remove from Select Design dropdown if it's the currently selected variation
      if (selectedVariationId === combinationId) {
        setSelectedTemplates((prev) => prev.filter((t) => t.value !== designId));
      }

      // In edit mode, refetch to ensure data consistency
      if (mode === "edit") {
        const refetchedData = await fetchAssignedTemplates();
        // Update selectedTemplates for currently selected variation after refetch
        if (selectedVariationId === combinationId && refetchedData) {
          const fetchedItem = refetchedData.find((f: any) => f.variantId === combinationId);
          if (fetchedItem) {
            const templates: TemplateOption[] = fetchedItem.templates.map((t: any) => {
              // Try to find in loaded templates first
              const template = loadedTemplates.get(t.template_id);
              if (template) {
                return {
                  ...template,
                  label: t.designDisplayName || template.label,
                  parentFolderName: t.parentFolderName ?? template.parentFolderName,
                };
              }
              // Use fetched template data
              return {
                value: t.template_id,
                label: t.designDisplayName || t.template_slug || t.template_id,
                category: t.category,
                parentFolderName: t.parentFolderName,
              };
            });
            setSelectedTemplates(templates);
          } else {
            // No templates left for this variation
            setSelectedTemplates([]);
          }
        }
      }

      showSuccess("Design assignment removed successfully!");
    } catch (err: any) {
      console.error("Unassign template error:", err);
      showError(err?.message || "Failed to unassign template");
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="designs-tab">
      <div className="bg-white border rounded-4 shadow-sm p-4 p-lg-5">
        {/* Header */}
        <div className="d-flex align-items-center gap-2 mb-3">
          <span className="d-inline-flex align-items-center justify-content-center designs-tab__icon">
            <Palette size={16} />
          </span>
          <h6 className="mb-0 fw-semibold">{title}</h6>
        </div>

        <hr className="my-3" />

        {/* Instruction */}
        <div className="alert designs-tab__alert mb-4" role="alert">
          Assign designs to product variations. First select a variation, then choose a design to apply to it.
        </div>

        {/* Main section */}
        <div className="row g-4 g-lg-5">
          {/* Left: Select Variation */}
          <div className="col-12 col-lg-6">
            <label className="form-label fw-semibold">
              Select Variation <span className="text-danger">*</span>
            </label>

            <select
              className="form-select designs-tab__select"
              value={selectedVariationId}
              onChange={(e) => handleVariationChange(e.target.value)}
              disabled={showEmptyCombinations}
            >
              <option value="">
                {showEmptyCombinations ? "No combinations available" : "Select a variation"}
              </option>
              {combinations.map((c) => (
                <option key={c.id} value={c.id}>
                  {formatVariationLabel(c.attributes, (c as any).variant_name)}
                </option>
              ))}
            </select>

            {showEmptyCombinations && (
              <div className="mt-2 small text-warning">
                Please generate combinations in the <b>Combinations</b> tab first
              </div>
            )}

            {/* Selected Variation Details */}
            {selectedVariation && (
              <div className="border rounded-4 mt-3 p-3 designs-tab__softCard">
                <div className="fs-14 fw-bold mb-2 small text-uppercase">
                  Selected Variation Details
                </div>

                <div className="d-flex flex-column gap-2">
                  {Object.entries(selectedVariation.attributes).map(([k, v]) => (
                    <div key={k} className="d-flex align-items-center justify-content-between">
                      <div className="fs-14 text-capitalize">{k}:</div>
                      <div className="fw-semibold small">{v}</div>
                    </div>
                  ))}

                  <div className="border-top pt-2 mt-1 d-flex align-items-center justify-content-between">
                    <div className="fs-14">Price:</div>
                    <div className="fw-semibold small">{money(selectedVariation.price)}</div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right: Select Design */}
          <div className="col-12 col-lg-6">
            <label className="form-label fw-semibold">
              Select Design <span className="text-danger">*</span>
            </label>
            <div className="designs-tab__select2Shell">
              <div className="designs-tab__select2Label text-muted small mb-1">Folders &amp; designs</div>
              {!treeItems.length ? (
                <div className="designs-tab__select2Menu p-2 text-muted small">Loading…</div>
              ) : (
                <div className="designs-tab__select2Menu designs-tab__tree" role="listbox" aria-label="Design folders and templates">
                  {treeItems.map((node) => {
                    if (node.kind === "folder") {
                      const isExpanded = expandedFolderIds.has(node.id);
                      const isLoading = loadingFolderIds.has(node.id);
                      return (
                        <button
                          type="button"
                          key={`folder-${node.id}-${node.depth}`}
                          className={`designs-tab__treeFolder designs-tab__select2Option ${isExpanded ? "is-open" : ""}`}
                          onClick={() => handleFolderToggle(node.id)}
                          style={{ paddingLeft: 8 + node.depth * 16 }}
                          disabled={isLoading}
                          title={isExpanded ? "Click to collapse" : "Click to expand"}
                        >
                          <span className={`designs-tab__treeChevron ${isExpanded ? "is-expanded" : ""}`} aria-hidden="true">
                            {isLoading ? "…" : "▸"}
                          </span>
                          <span className="designs-tab__treeLabel">{node.label}</span>
                        </button>
                      );
                    }

                    const isSelected = selectedTemplates.some((t) => t.value === node.id);
                    return (
                      <button
                        type="button"
                        key={`design-${node.id}-${node.depth}`}
                        className={`designs-tab__treeDesign designs-tab__select2Option ${isSelected ? "is-selected" : ""}`}
                        onClick={() =>
                          toggleDesignSelection({
                            id: node.id,
                            label: node.label,
                            category: node.category,
                            previewUrl: node.previewUrl,
                            parentFolderName: node.parentFolderName,
                          })
                        }
                        style={{ paddingLeft: 8 + node.depth * 16 }}
                        disabled={!selectedVariationId}
                        title={!selectedVariationId ? "Select a variation first" : node.label}
                      >
                        <span className="designs-tab__treeDesignCheck">{isSelected ? "✓" : ""}</span>
                        <span className="designs-tab__treeDesignThumbWrap">
                          {node.previewUrl ? (
                            <img className="designs-tab__treeDesignThumb" src={node.previewUrl} alt={node.label} />
                          ) : (
                            <span className="designs-tab__treeDesignThumbPlaceholder">No preview</span>
                          )}
                        </span>
                        <span className="designs-tab__treeLabel designs-tab__treeDesignLabel">{node.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}

              {!selectedVariationId && (
                <div className="mt-2 small text-muted">Select a variation to assign designs.</div>
              )}
            </div>

            {/* Assigned Designs card */}
            {selectedVariationId && assignedDesignsForSelectedVariation.length > 0 && (
              <div className="border rounded-4 mt-3 p-3 designs-tab__successCard">
                <div className="text-uppercase small fw-semibold mb-2 designs-tab__successTitle">
                  Assigned Designs ({assignedDesignsForSelectedVariation.length})
                </div>

                {assignedDesignsForSelectedVariation.map((design, idx) => (
                  <div key={design.id} className={idx > 0 ? "mt-2" : ""}>
                    <div className="fw-semibold designs-tab__successTitle">{design.name}</div>
                    {design.parentFolderName?.trim() && (
                      <div className="designs-tab__successSub mt-1">
                        Folder: {design.parentFolderName}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Summary */}
        <hr className="my-4 my-lg-5" />

        <div className="fw-semibold mb-3">Design Assignments Summary</div>

        <div className="border rounded-4 overflow-hidden">
          <table className="table mb-0 align-middle">
            <thead className="table-light">
              <tr className="text-uppercase small text-muted">
                {/* <th style={{ width: 90 }}>S.NO</th> */}
                <th>Variation</th>
                <th style={{ width: 240 }}>Design</th>
                <th style={{ width: 180 }}>Folder</th>
                <th style={{ width: 80 }}>Actions</th>
              </tr>
            </thead>

            <tbody>
              {loadingAssignments ? (
                <tr>
                  <td colSpan={4} className="text-center py-4">
                    <div className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></div>
                    Loading assignments...
                  </td>
                </tr>
              ) : summaryRows.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-center py-4 text-muted">
                    No design assignments yet
                  </td>
                </tr>
              ) : (
                summaryRows.map(({ combo, design }, idx) => {
                  const deleteKey = `${combo.id}-${design.id}`;
                  const isDeleting = deleting === deleteKey;
                  return (
                    <tr key={`${combo.id}-${design.id}`}>
                      {/* <td className="fw-semibold">
                        {combo.sno ?? idx + 1}
                      </td> */}
                      <td>{formatVariationLabel(combo.attributes, (combo as any).variant_name)}</td>
                      <td className="fw-semibold">{design.name}</td>
                      <td>
                        {design.parentFolderName?.trim() ? (
                          <span className="badge designs-tab__pill fs-13 px-3 py-2 rounded-pill">
                            {design.parentFolderName}
                          </span>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td className="text-center">
                        <DeleteIconButton
                          type="button"
                          className="btn btn-outline-danger btn-sm"
                          onClick={() => handleDeleteAssignment(combo.id, design.id)}
                          disabled={isDeleting}
                          title="Remove assignment"
                        />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Assign Designs Button */}
        <div className="d-flex justify-content-end mt-4">
          <button
            type="button"
            className="btn btn-primary table-addbutton table-addbutton  save-btn surface-btn fw-bold fs-16"
            onClick={handleAssignDesigns}
            disabled={assigning || assignments.length === 0 || !productId}
          >
            {assigning ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Assigning...
              </>
            ) : (
              "Assign Designs"
            )}
          </button>
        </div>
      </div>
      {
        <style>
          {`
            /* Designs Tab */
            .designs-tab__icon {
              width: 26px;
              height: 26px;
              border-radius: 50%;
              background: #eef2ff; /* light indigo */
              color: #4f46e5;      /* indigo */
            }
                    
            .designs-tab__alert {
              background: #eef4ff;         /* light blue */
              border: 1px solid #cfe0ff;
              color: #1e3a8a;
              border-radius: 12px;
            }
                    
            .designs-tab__select {
              background: #f3f4f6;  /* light gray like screenshot */
              border-radius: 12px;
              padding-top: 10px;
              padding-bottom: 10px;
            }
                    
            .designs-tab__softCard {
              background: #f8fafc;  /* soft gray */
            }
                    
            .designs-tab__successCard {
              background: #ecfdf5;
              border-color: #bbf7d0 !important;
            }
                    
            .designs-tab__successTitle {
              color: #166534;
            }
                    
            .designs-tab__successSub {
              color: #15803d;
            }
                    
            .designs-tab__pill {
              background: #e0e7ff; /* indigo pill */
              color: #3730a3;
              font-weight: 600;
            }

            /* Select2-like folder / design list */
            .designs-tab__select2Shell {
              border: 1px solid #ccc;
              border-radius: 4px;
              background: #fff;
              box-shadow: 0 1px 0 rgba(0,0,0,0.06);
            }

            .designs-tab__select2Label {
              padding: 6px 10px 0;
            }

            .designs-tab__select2Menu {
              background: #fff;
              border-top: 1px solid #e5e7eb;
              border-radius: 0 0 4px 4px;
            }

            .designs-tab__tree {
              display: flex;
              flex-direction: column;
              gap: 16px;
              padding: 16px 12px;
              max-height: 560px;
              overflow: auto;
            }

            .designs-tab__select2Option {
              border: 1px solid #e8e8e8;
              border-radius: 8px;
              background: #fff;
              font-size: 14px;
              line-height: 1.45;
              box-sizing: border-box;
            }

            .designs-tab__treeFolder {
              display: flex;
              align-items: center;
              gap: 10px;
              width: 100%;
              min-height: 48px;
              padding: 12px 14px;
              cursor: pointer;
              text-align: left;
              color: #333;
              font-weight: 500;
            }

            .designs-tab__treeFolder:hover,
            .designs-tab__treeFolder.is-open {
              background: #f6f6f6;
            }

            .designs-tab__treeChevron {
              width: 16px;
              flex: 0 0 16px;
              display: inline-flex;
              align-items: center;
              justify-content: center;
              color: #888;
              font-size: 12px;
              transition: transform 0.12s ease;
              transform: rotate(0deg);
            }

            .designs-tab__treeChevron.is-expanded {
              transform: rotate(90deg);
            }

            .designs-tab__treeLabel {
              flex: 1;
              min-width: 0;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
              font-weight: inherit;
            }

            .designs-tab__treeDesign {
              display: flex;
              align-items: center;
              gap: 16px;
              width: 100%;
              min-height: 164px;
              padding: 12px 14px;
              cursor: pointer;
              text-align: left;
              color: #333;
              transition: background 0.12s ease;
            }

            .designs-tab__treeDesign:hover {
              background: #f6f6f6;
            }

            .designs-tab__treeDesign.is-selected {
              background: #e7f4ff;
            }

            .designs-tab__treeDesignCheck {
              width: 28px;
              height: 28px;
              display: inline-flex;
              align-items: center;
              justify-content: center;
              border-radius: 8px;
              background: rgba(236, 72, 153, 0.12);
              color: #ec4899;
              font-weight: 900;
              flex: 0 0 auto;
              font-size: 15px;
            }

            .designs-tab__treeDesignThumbWrap {
              width: 112px;
              height: 140px;
              border-radius: 8px;
              overflow: hidden;
              border: 1px solid rgba(229, 231, 235, 0.9);
              background: #ffffff;
              flex: 0 0 auto;
              display: inline-flex;
              align-items: center;
              justify-content: center;
            }

            .designs-tab__treeDesignThumb {
              width: 100%;
              height: 100%;
              object-fit: cover;
              display: block;
            }

            .designs-tab__treeDesignThumbPlaceholder {
              font-size: 12px;
              color: #9ca3af;
              padding: 8px;
              text-align: center;
              line-height: 1.3;
            }

            .designs-tab__treeDesignLabel {
              max-width: 280px;
              font-size: 14px;
              line-height: 1.4;
            }
          `}
        </style>
      }
    </div>
  );
};

export default DesignsTab;